"use client";

import { useEffect, useRef, useState } from "react";
import { CountrySelect } from "./CountrySelect";

const labelCls = "text-[11px] uppercase tracking-[1.6px] text-ink-soft";
const inputCls = "w-full border-b border-ink/30 bg-transparent py-2 text-[15px] text-ink outline-none focus:border-ink";

// region = Google administrative_area_level_1 (AU state / NZ region) — only ever set by an
// autocomplete pick; a manual edit to city/postcode/country clears it so it can't go stale.
type Addr = { line1: string; city: string; postcode: string; countryIso: string; region?: string };

// Manual address fields are ALWAYS shown (with autocomplete tokens, so the browser's
// own autofill works). When a Google Maps key is present, a Places search bar sits on
// top; selecting a result fills the fields and the search bar disappears. Country is
// last, under city/postcode.
// onSelect (optional) fires when a Google Places suggestion is chosen, with the
// fully-resolved address — lets the parent persist it immediately (the manual
// onChange path only updates state). Falls back to onChange when absent.
export function AddressFields({ value, onChange, onSelect, required }: { value: Addr; onChange: (a: Addr) => void; onSelect?: (a: Addr) => void; required?: boolean }) {
  const key = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
  const [ready, setReady] = useState(false);
  const [searchHidden, setSearchHidden] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;

    // DEFERRED (owner, 2026-09-03 — "should load after the page load"). The Maps
    // bootstrap is ~244 KB across places.js + main.js + util.js and used to be
    // injected the moment this mounted, competing with the app bundle for a
    // narrow mobile pipe: measured on a 1.6 Mbps connection, places.js did not
    // finish until ~6.3s. The autocomplete is a convenience on a field that sits
    // far down a long page — the manual inputs below always render and always
    // work without it — so nothing is lost by waiting until the page has settled
    // or the customer actually approaches the field, whichever comes first.
    let started = false;
    const startOnce = () => { if (!started) { started = true; void init(); } };

    async function init() {
      try {
        // Inject the JS API bootstrap once. With loading=async the `places`
        // library is NOT ready at script onload — we must await importLibrary,
        // otherwise `google.maps.places.Autocomplete` is undefined (the bug).
        if (!w.google?.maps?.importLibrary && !document.getElementById("gmaps-places")) {
          const s = document.createElement("script");
          s.id = "gmaps-places";
          s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&v=weekly&libraries=places&loading=async`;
          s.async = true;
          document.head.appendChild(s);
        }
        // Wait for the bootstrap to expose importLibrary, then load Places.
        const start = Date.now();
        while (!w.google?.maps?.importLibrary) {
          if (Date.now() - start > 10000) return; // give up; manual fields stay usable
          await new Promise((r) => setTimeout(r, 100));
        }
        await w.google.maps.importLibrary("places");
        if (!cancelled) setReady(true);
      } catch {
        /* Places unavailable — the manual address fields below still work. */
      }
    }
    // Whichever fires first: the page going idle after load, or the field coming
    // near the viewport. The observer matters for someone who jumps straight to
    // #reserve — they must not wait on an idle callback that may never come on a
    // busy main thread.
    const idle = () => {
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
      if (ric) ric(startOnce, { timeout: 4000 }); else setTimeout(startOnce, 1500);
    };
    if (document.readyState === "complete") idle();
    else window.addEventListener("load", idle, { once: true });

    let io: IntersectionObserver | undefined;
    const host = inputRef.current?.closest("div") ?? inputRef.current;
    if (host && "IntersectionObserver" in window) {
      io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { startOnce(); io?.disconnect(); }
      }, { rootMargin: "600px" });
      io.observe(host);
    }

    return () => {
      cancelled = true;
      io?.disconnect();
      window.removeEventListener("load", idle);
    };
  }, [key]);

  useEffect(() => {
    if (!ready || searchHidden || !inputRef.current) return;
    const g = (window as unknown as { google: any }).google; // eslint-disable-line @typescript-eslint/no-explicit-any
    if (!g?.maps?.places?.Autocomplete) return;
    const ac = new g.maps.places.Autocomplete(inputRef.current, { types: ["address"], fields: ["address_components"] });
    const listener = ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      const get = (type: string, short = false) => {
        const c = (place.address_components ?? []).find((x: { types: string[] }) => x.types.includes(type));
        return c ? (short ? c.short_name : c.long_name) : "";
      };
      const next: Addr = {
        line1: `${get("street_number")} ${get("route")}`.trim(),
        city: get("locality") || get("postal_town") || get("sublocality") || "",
        postcode: get("postal_code"),
        countryIso: get("country", true) || value.countryIso,
        region: get("administrative_area_level_1", true) || undefined,
      };
      // onSelect persists immediately; without it, fall back to onChange.
      if (onSelect) onSelect(next); else onChange(next);
      setSearchHidden(true);
    });
    return () => listener?.remove?.();
  }, [ready, searchHidden]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col gap-6">
      {key && !searchHidden && (
        <label className="flex flex-col gap-1.5">
          <span className={labelCls}>Find your address</span>
          <input ref={inputRef} autoComplete="off" placeholder="Start typing your address…" className={inputCls} />
          <span className="text-[12px] font-light text-ink-soft">Or enter it manually below.</span>
        </label>
      )}

      <div className="grid grid-cols-2 gap-x-8 gap-y-6">
        <label className="col-span-2 flex flex-col gap-1.5">
          <span className={labelCls}>Address{required && <span className="text-corten"> *</span>}</span>
          <input autoComplete="street-address" value={value.line1} onChange={(e) => onChange({ ...value, line1: e.target.value })} placeholder="12 Lakefront Drive" className={inputCls} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={labelCls}>City{required && <span className="text-corten"> *</span>}</span>
          <input autoComplete="address-level2" value={value.city} onChange={(e) => onChange({ ...value, city: e.target.value, region: undefined })} placeholder="Queenstown" className={inputCls} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={labelCls}>Postcode</span>
          <input autoComplete="postal-code" value={value.postcode} onChange={(e) => onChange({ ...value, postcode: e.target.value, region: undefined })} placeholder="9300" className={inputCls} />
        </label>
        <div className="col-span-2">
          <span className={labelCls}>Country</span>
          <CountrySelect value={value.countryIso} mode="name" autoComplete="country" name="country" onChange={(c) => onChange({ ...value, countryIso: c.iso, region: undefined })} />
        </div>
      </div>
    </div>
  );
}
